import { useState, useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { Attachment } from '@sdk/attachments/types';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { Persona } from '@sdk/personaCollection/types/Persona';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import DisplayText from '@sdk/components/ui/DisplayText';
import Picker from '@sdk/components/forms/Picker';
import { NewsfeedPage } from '@sdk/newsfeed/types';

import TextEditor from '@web/richText/TextEditor';

const PageSelectContainer = styled(Row)`
  align-items: center;
`;

type Props = {
  pages: NewsfeedPage[];
  canSelectPage?: boolean;
  personas: Persona[];
  initialValue?: string;
  initialAttachments?: Attachment[];
  initialPageId?: number;
  onSubmit?: (contentObj: { text: string; attachments: Attachment[]; pageId: number }) => void;
  onContentChange?: (content: {
    text?: string;
    attachments?: Attachment[];
    pageId?: number;
  }) => void;
  hideSubmit?: boolean;
  placeholder?: string;
  resizable?: boolean;
  showErrorToast?: (msg: string) => void;
};

const ArticleEditor: React.FC<React.PropsWithChildren<Props>> = ({
  onSubmit,
  pages,
  canSelectPage,
  personas,
  onContentChange,
  initialValue,
  initialAttachments,
  initialPageId,
  hideSubmit,
  placeholder,
  resizable,
}) => {
  const [selectedPageId, setSelectedPageId] = useState(() => {
    if (initialPageId) {
      return initialPageId;
    }

    return canSelectPage ? pages?.[0]?.id : null;
  });
  const pagesOptions = pages?.map((p) => ({ value: p.id, label: p.name })) || [];

  const postArticle = (content: { text: string; attachments: Attachment[] }): void => {
    onSubmit?.({ ...content, pageId: selectedPageId });
  };

  const onTextEditorContentChange = (content: {
    text: string;
    attachments: Attachment[];
  }): void => {
    onContentChange({ ...content, pageId: selectedPageId });
  };

  useEffect(() => {
    if (canSelectPage) {
      onContentChange({ pageId: selectedPageId });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPageId, canSelectPage]);

  return (
    <Column>
      {canSelectPage && (
        <PageSelectContainer mBottom={10}>
          <DisplayText fontSize={14} themeColor={ThemeColorType.gray} opacity={0.5}>
            {gettext('Post to')}
          </DisplayText>
          <Row>
            <Picker
              noBorder
              options={pagesOptions}
              value={selectedPageId}
              onChange={(v): void => setSelectedPageId(v as number)}
            />
          </Row>
        </PageSelectContainer>
      )}
      <TextEditor
        gifPickerEnabled={false}
        onSubmit={postArticle}
        submitLabel={gettext('Post')}
        personas={personas}
        placeholder={placeholder}
        initialValue={initialValue}
        initialAttachments={initialAttachments}
        onContentChange={onTextEditorContentChange}
        hideSubmit={hideSubmit}
        resizable={resizable}
        submitOnEnter={false}
        emojiPickerPosition="bottom"
      />
    </Column>
  );
};

export default ArticleEditor;
